import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EmailIcon from '@mui/icons-material/Email'
import EditIcon from '@mui/icons-material/Edit'
import PeopleIcon from '@mui/icons-material/People'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PaymentsIcon from '@mui/icons-material/Payments'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import BusinessIcon from '@mui/icons-material/Business'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import TerminalIcon from '@mui/icons-material/Terminal'
import InsightsIcon from '@mui/icons-material/Insights'
import TimelineIcon from '@mui/icons-material/Timeline'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import StatTile from '../kit/StatTile'
import SectionCard from '../kit/SectionCard'
import KeyValueList from '../kit/KeyValueList'
import ConfirmDialog from '../kit/ConfirmDialog'
import ActivityTimeline from '../kit/ActivityTimeline'
import { InterestChip, SoftChip, StageChip, TrialledChip } from '../kit/Chips'
import {
  STAGES,
  followUpState,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPeriod,
  fromNow,
  monthlyFee,
  subscriptionChip,
  tenantUrl,
  tierLabel,
} from '../kit/helpers'
import {
  ALLOWANCE_METRICS,
  deriveContractFromTenant,
  hasActiveSubscription,
  legacyAgreedLimits,
  planAllowanceForTier,
} from './tenantHelpers'
import CommercialUsagePanel from './CommercialUsage/CommercialUsagePanel'
import {
  ACTION_STATUS,
  OPEN_ACTION_STATUSES,
  sequenceStatusMeta,
} from './CommercialUsage/constants'
import UserAccounts from './UserAccounts'
import BulkEmployeeImport from './BulkUpload/BulkEmployeeImport'
import ContractDialog from './ContractDialog'
import QuotedSubscriptionDialog from './QuotedSubscriptionDialog'
import LinkLeadDialog from '../Sales/LinkLeadDialog'

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'contract', label: 'Contract & billing' },
  { key: 'usage', label: 'Commercial usage' },
  { key: 'users', label: 'Users' },
  { key: 'sales', label: 'Sales' },
  { key: 'tools', label: 'Tools' },
  { key: 'activity', label: 'Activity' },
]

const CADENCE_LABELS = { monthly: 'Monthly', annually: 'Annually' }

// One allowance on the contract card: what is stored, or the plan default
// where nothing of its own is stored. Effective figures (with complimentary
// capacity and seats) live on the Commercial usage tab.
const contractLimitText = (tenant, displayContract, metric) => {
  const included = displayContract.includedLimits?.[metric.key]
  const additional = displayContract.additionalLimits?.[metric.key]
  const planDefault = planAllowanceForTier(tenant.subscriptionTier, metric.key)
  const parts = []
  if (included !== '' && included !== undefined) parts.push(`${formatNumber(included)} included`)
  else if (planDefault !== null) parts.push(`${formatNumber(planDefault)} included (plan default)`)
  if (additional !== '' && additional !== undefined) parts.push(`${formatNumber(additional)} additional`)
  return parts.length ? parts.join(' + ') : 'Not included in plan'
}

const SingleTenant = () => {
  const { tenantID } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tenant, setTenant] = useState(null)
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)
  // What the Commercial usage tab last loaded ({ tenantID, status, ... }); until
  // it has been opened the header falls back to the contract's cached summary.
  const [usageSummary, setUsageSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [confirm, setConfirm] = useState(null) // 'lost' | 'delete'
  const [deleteLead, setDeleteLead] = useState(false)
  const [contractOpen, setContractOpen] = useState(false)
  const [quotedOpen, setQuotedOpen] = useState(false)
  const [linkLeadOpen, setLinkLeadOpen] = useState(false)

  const tab = searchParams.get('tab') || 'overview'
  const setTab = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'overview') next.delete('tab')
    else next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  // Tenant documents returned by the save endpoints are not populated, so the
  // notes and the linked lead already loaded are kept when one is merged in.
  const mergeTenant = (updated) =>
    setTenant((prev) => ({
      ...prev,
      ...updated,
      comments: prev.comments,
      linkedCRMRecord: prev.linkedCRMRecord,
    }))

  // Re-reads the tenant without the page-level spinner: after a commercial
  // usage action the contract, its review summary and the activity timeline
  // have changed on the server.
  const reloadTenant = () =>
    API.get(`/admincrm/tenants/${tenantID}`)
      .then((res) => {
        if (res.status === 200 && res.data) setTenant(res.data)
      })
      .catch(() => {})

  const absorbUsagePanel = (panel) =>
    setUsageSummary({
      tenantID,
      status: panel.sequence?.status,
      occurrence: panel.sequence?.currentOccurrenceCount,
      lastExceededPeriod: panel.sequence?.lastExceededPeriod,
      requiredActions: (panel.requiredActions ?? []).length,
    })

  useEffect(() => {
    setLoading(true)
    API.get(`/admincrm/tenants/${tenantID}`)
      .then((res) => {
        if (res.status === 200 && res.data) setTenant(res.data)
        else setError(res.data?.message || 'Could not load this tenant.')
      })
      .finally(() => setLoading(false))
    setUsersLoading(true)
    API.get(`/admincrm/tenant-users/${tenantID}`)
      .then((res) => {
        if (res.status === 200) setUsers(res.data)
      })
      .finally(() => setUsersLoading(false))
  }, [tenantID])

  const linkedLead = tenant?.linkedCRMRecord
  const activeSubscription = hasActiveSubscription(tenant)
  const displayContract = useMemo(() => deriveContractFromTenant(tenant ?? {}), [tenant])
  const salesStage = linkedLead?.stage ?? (activeSubscription ? 'Converted' : tenant?.isActive === false ? 'Lost' : 'New')

  const activeUsers = useMemo(() => users.filter((user) => !user.security?.archived), [users])
  const siteAdmins = useMemo(
    () => activeUsers.filter((user) => user.security?.isSiteAdmin || user.security?.isTenantOwner),
    [activeUsers],
  )
  const lastActivity = useMemo(
    () =>
      users.reduce((latest, user) => {
        if (!user.lastActive) return latest
        return !latest || new Date(user.lastActive) > new Date(latest) ? user.lastActive : latest
      }, null),
    [users],
  )

  const mailto = (emails, subject) => {
    const recipients = Array.isArray(emails) ? emails.filter(Boolean).join(',') : emails
    if (!recipients) return
    window.location.href = `mailto:${recipients}?subject=${encodeURIComponent(subject)}`
  }

  const changeStage = async (stage) => {
    if (stage === 'Lost') {
      setConfirm('lost')
      return
    }
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/customer-stage', {
      tenantId: tenantID,
      leadId: linkedLead?._id,
      stage,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not update the stage.')
      return
    }
    setTenant((prev) => ({
      ...prev,
      isActive: res.data?.tenantIsActive === undefined ? prev.isActive : res.data.tenantIsActive,
      linkedCRMRecord: res.data,
    }))
  }

  const hasLiveStripeSubscription =
    !!tenant?.stripeSubscriptionId &&
    ['active', 'trialing', 'past_due', 'unpaid'].includes(
      String(tenant?.stripeSubscriptionStatus || '').toLowerCase(),
    )

  const toggleManualBilling = async (enabled) => {
    setBusy(true)
    setError('')
    const res = await API.put(`/admincrm/tenants/${tenantID}/manual-billing`, {
      enabled,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not update manual billing.')
      return
    }
    mergeTenant(res.data)
  }

  const togglePaymentGrace = async (shortGrace) => {
    setBusy(true)
    setError('')
    const res = await API.put(`/admincrm/tenants/${tenantID}/payment-grace`, {
      shortGrace,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not update the payment grace.')
      return
    }
    mergeTenant(res.data)
  }

  const markLost = async () => {
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/tenant-lost', { tenantId: tenantID })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not mark this tenant as lost.')
      return
    }
    setTenant((prev) => ({ ...prev, isActive: false, linkedCRMRecord: res.data.lead }))
    setConfirm(null)
  }

  const [confirmation, setConfirmation] = useState('')
  const deleteTenant = async () => {
    setBusy(true)
    setError('')
    const res = await API.post('/admincrm/sales/delete-tenant-customer', {
      tenantId: tenantID,
      deleteLead,
      confirmation,
    })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not delete this tenant.')
      return
    }
    navigate('/tenants')
  }

  const openOrCreateLead = async () => {
    if (linkedLead?._id) {
      navigate(`/sales/${linkedLead._id}`)
      return
    }
    setBusy(true)
    const res = await API.post('/admincrm/sales/tenant-lead', { tenantId: tenantID })
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not create the sales lead.')
      return
    }
    navigate(`/sales/${res.data._id}`)
  }

  const ensureStripeCustomer = async () => {
    setBusy(true)
    setError('')
    const res = await API.post(`/admincrm/tenants/${tenantID}/stripe-customer`)
    setBusy(false)
    if (res.status !== 200) {
      setError(res.data?.message || res.data?.error || 'Could not create the Stripe customer.')
      return
    }
    mergeTenant(res.data.tenant)
  }

  const openStripe = () => {
    const subscriptionId = displayContract.stripe?.subscriptionId || tenant.stripeSubscriptionId
    const customerId = displayContract.stripe?.customerId || tenant.stripeCustomerRef
    const stripeId = subscriptionId || customerId
    if (!stripeId) return
    window.open(`https://dashboard.stripe.com/${subscriptionId ? 'subscriptions' : 'customers'}/${stripeId}`, '_blank')
  }

  const logActivity = async ({ kind, note }) => {
    const res = await API.post(`/admincrm/postcomment/tenant/${tenantID}`, { comment: note, kind })
    if (res.status !== 200) {
      setError(res.data?.message || 'Could not add the note.')
      throw new Error('comment failed')
    }
    setTenant((prev) => ({ ...prev, comments: [res.data, ...(prev.comments ?? [])] }))
  }

  if (loading && !tenant) return <LinearProgress />
  if (!tenant) {
    return (
      <Stack gap={2}>
        <PageHeader back="/tenants" eyebrow="Tenant" title="Tenant not found" />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    )
  }

  const chip = subscriptionChip({ ...tenant, activeSubscription })
  const renewal = displayContract.renewalDate || tenant.stripeSubscriptionExpiry
  const fee = monthlyFee(tenant.contract)
  const hasStripe = Boolean(
    displayContract.stripe?.subscriptionId ||
      tenant.stripeSubscriptionId ||
      displayContract.stripe?.customerId ||
      tenant.stripeCustomerRef,
  )
  const address = [
    tenant.companyAddress?.lineOne,
    tenant.companyAddress?.lineTwo,
    tenant.companyAddress?.city,
    tenant.companyAddress?.county,
    tenant.companyAddress?.postCode,
  ]
    .filter(Boolean)
    .join(', ')

  // Commercial usage summary for the header tile and the tab label: from the
  // usage tab once it has loaded, otherwise the summary cached on the contract.
  const usageReview = tenant.contract?.usageReview ?? {}
  const loadedUsage = usageSummary?.tenantID === tenantID ? usageSummary : null
  const usageStatusKey = loadedUsage?.status ?? usageReview.status ?? 'clear'
  const usageStatus = sequenceStatusMeta(usageStatusKey)
  const usageOccurrence = loadedUsage ? loadedUsage.occurrence : usageReview.currentOccurrenceCount
  const usageLastExceeded = loadedUsage ? loadedUsage.lastExceededPeriod : usageReview.lastExceededPeriod
  const usageOpen = OPEN_ACTION_STATUSES.includes(usageStatusKey)
  const usageCaption =
    [
      usageOpen ? ACTION_STATUS[usageStatusKey]?.plain : usageStatusKey === 'resolved' ? 'no open action' : '',
      usageOccurrence > 0 ? `occurrence ${usageOccurrence}` : '',
      usageLastExceeded ? `last exceeded ${formatPeriod(usageLastExceeded)}` : '',
    ]
      .filter(Boolean)
      .join(' · ')
      .replace(/^./, (first) => first.toUpperCase()) || 'No exceeded months in the review sequence'
  // The number of open actions is only known once the tab has loaded.
  const usageBadge = loadedUsage ? loadedUsage.requiredActions || '' : usageOpen ? '!' : ''
  const legacyLimits = legacyAgreedLimits(tenant)
  const acceptedTier = tenant.contract?.includedLimitsTier
  const planChangedSinceAcceptance =
    acceptedTier !== null && acceptedTier !== undefined && Number(acceptedTier) !== Number(tenant.subscriptionTier)

  return (
    <Stack gap={2.5}>
      {(loading || busy) && <LinearProgress sx={{ borderRadius: 1 }} />}
      <PageHeader
        back="/tenants"
        eyebrow="Tenant"
        title={tenant.name}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              disabled={!tenant.mainContact?.email}
              onClick={() => mailto(tenant.mainContact?.email, `AmbuNet - ${tenant.name}`)}
            >
              Email contact
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              disabled={!siteAdmins.length}
              onClick={() =>
                mailto(
                  siteAdmins.map((user) => user.email),
                  `AmbuNet - ${tenant.name}`,
                )
              }
            >
              Email site admins
            </Button>
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon />}
              disabled={!tenant.subdomain}
              onClick={() => window.open(tenantUrl(tenant.subdomain), '_blank')}
            >
              Visit site
            </Button>
            <IconButton
              aria-label="More actions"
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}
            >
              <MoreVertIcon />
            </IconButton>
          </>
        }
      >
        <Stack direction="row" gap={0.75} flexWrap="wrap" marginTop={1} alignItems="center">
          <Chip label={chip.label} color={chip.color} variant={chip.variant ?? 'filled'} size="small" sx={{ borderRadius: 1.5, fontWeight: 700 }} />
          {activeSubscription && (
            <SoftChip color="#239AA4" label={displayContract.packageName || tierLabel(tenant.subscriptionTier)} />
          )}
          <StageChip stage={salesStage} />
          {tenant.trialEnd && <TrialledChip trialEnd={tenant.trialEnd} />}
          {tenant.region && <SoftChip color="#64748B" label={tenant.region} />}
          <Link
            href={tenantUrl(tenant.subdomain)}
            target="_blank"
            rel="noreferrer"
            variant="caption"
            color="text.secondary"
          >
            {tenant.subdomain}.{process.env.ROOT_DOMAIN}
          </Link>
        </Stack>
      </PageHeader>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {tenant.isActive === false && (
        <Alert severity="warning">
          This tenant is disabled - nobody can log in. Move the sales stage away from Lost to
          re-enable it.
        </Alert>
      )}
      {tenant.manualBilling === true && (
        <Alert
          severity={
            !displayContract.renewalDate
              ? 'warning'
              : moment(displayContract.renewalDate).isBefore(moment(), 'day')
                ? 'error'
                : moment(displayContract.renewalDate).diff(moment(), 'days') <= 30
                  ? 'warning'
                  : 'info'
          }
        >
          Manual billing - access never expires automatically; disabling the tenant is the only
          off switch.{' '}
          {displayContract.renewalDate
            ? moment(displayContract.renewalDate).isBefore(moment(), 'day')
              ? `The contract renewal date (${formatDate(displayContract.renewalDate)}) has passed - invoice the customer and roll it forward on the contract.`
              : `Renewal due ${formatDate(displayContract.renewalDate)} - billing is reminded 30 and 7 days ahead.`
            : 'No renewal date is recorded on the contract, so no renewal reminders can be sent - add one via Edit contract.'}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Users"
            value={usersLoading ? undefined : activeUsers.length}
            caption={`${siteAdmins.length} site admin${siteAdmins.length === 1 ? '' : 's'}`}
            icon={<PeopleIcon />}
            tone="#268EBB"
            onClick={() => setTab('users')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Last activity"
            value={usersLoading ? undefined : lastActivity ? fromNow(lastActivity) : 'Never'}
            caption={lastActivity ? formatDate(lastActivity) : 'No user has logged in'}
            icon={<AccessTimeIcon />}
            tone={lastActivity ? (moment().diff(moment(lastActivity), 'days') <= 7 ? '#00964C' : '#F28B1F') : '#64748B'}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Monthly fee"
            value={fee === null ? '—' : formatCurrency(fee, displayContract.currency)}
            caption={
              displayContract.billingCadence?.startsWith('annual')
                ? `Billed annually · ${formatCurrency(displayContract.annualFee, displayContract.currency)}`
                : 'Billed monthly'
            }
            icon={<PaymentsIcon />}
            tone="#239AA4"
            onClick={() => setTab('contract')}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Renewal"
            value={renewal ? formatDate(renewal) : '—'}
            caption={renewal ? (moment(renewal).isBefore(moment()) ? `Expired ${fromNow(renewal)}` : `Renews ${fromNow(renewal)}`) : 'No renewal date'}
            icon={<EventRepeatIcon />}
            tone={renewal && moment(renewal).diff(moment(), 'days') <= 14 ? '#E32525' : '#64748B'}
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          {/* The value is a single word, so it cannot wrap: it scales with the tile. */}
          <StatTile
            label="Commercial usage"
            value={usageStatus.short}
            caption={usageCaption}
            icon={<QueryStatsIcon />}
            tone={usageStatus.color}
            onClick={() => setTab('usage')}
            sx={{ '& .MuiTypography-h4': { fontSize: 'clamp(1.25rem, 2.1vw, 2.125rem)' } }}
          />
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: '1px solid #E2E8F0' }}>
        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 }, minHeight: 44 }}
        >
          {TABS.map((option) => (
            <Tab
              key={option.key}
              value={option.key}
              label={
                option.key === 'users'
                  ? `Users (${activeUsers.length})`
                  : option.key === 'usage' && usageBadge
                    ? `${option.label} (${usageBadge})`
                    : option.label
              }
            />
          ))}
        </Tabs>
      </Box>

      {tab === 'overview' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} xl={4}>
            <SectionCard title="Customer" icon={<BusinessIcon />}>
              <KeyValueList
                rows={[
                  {
                    label: 'Main contact',
                    value: [tenant.mainContact?.firstName, tenant.mainContact?.lastName].filter(Boolean).join(' '),
                  },
                  {
                    label: 'Email',
                    value: tenant.mainContact?.email,
                    render: tenant.mainContact?.email ? (
                      <Link href={`mailto:${tenant.mainContact.email}`} variant="body2">
                        {tenant.mainContact.email}
                      </Link>
                    ) : null,
                  },
                  { label: 'Phone', value: tenant.mainContact?.phone },
                  { label: 'Company phone', value: tenant.companyPhone },
                  {
                    label: 'Website',
                    value: tenant.companyWebsite,
                    render: tenant.companyWebsite ? (
                      <Link href={/^https?:/.test(tenant.companyWebsite) ? tenant.companyWebsite : `https://${tenant.companyWebsite}`} target="_blank" rel="noreferrer" variant="body2">
                        {tenant.companyWebsite}
                      </Link>
                    ) : null,
                  },
                  { label: 'Company number', value: tenant.companyNumber },
                  { label: 'Address', value: address, fullWidth: true },
                  { label: 'Signed up', value: `${formatDate(tenant.createdAt)} (${fromNow(tenant.createdAt)})` },
                ]}
              />
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <SectionCard
              title="Subscription"
              icon={<CreditCardIcon />}
              action={
                hasStripe ? (
                  <Button size="small" endIcon={<OpenInNewIcon />} onClick={openStripe}>
                    Stripe
                  </Button>
                ) : null
              }
            >
              <KeyValueList
                showEmpty
                rows={[
                  { label: 'Status', value: chip.label },
                  { label: 'Tier', value: tierLabel(tenant.subscriptionTier) },
                  { label: 'Package', value: displayContract.packageName },
                  { label: 'Stripe status', value: tenant.stripeSubscriptionStatus },
                  { label: 'Expiry', value: formatDate(tenant.stripeSubscriptionExpiry) },
                  { label: 'Cancellation', value: formatDate(tenant.subscriptionCancellationDate) },
                  { label: 'Payment grace until', value: formatDate(tenant.stripePaymentGraceUntil) },
                  { label: 'Last payment', value: formatDate(tenant.stripeLastPaymentSucceededAt) },
                  { label: 'Trial ended (legacy)', value: formatDate(tenant.trialEnd) },
                  { label: 'Stripe customer', value: tenant.stripeCustomerRef || displayContract.stripe?.customerId, fullWidth: true },
                ]}
              />
              {!activeSubscription && !tenant.isDemo && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No active subscription. This tenant is a cleanup candidate - capture it into the
                  pipeline (Sales tab) before deleting.
                </Alert>
              )}
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <SectionCard title="Technical" icon={<TerminalIcon />}>
              <KeyValueList
                showEmpty
                rows={[
                  { label: 'Subdomain', value: tenant.subdomain },
                  { label: 'Region', value: tenant.region },
                  { label: 'Database', value: tenant.dbConnection, fullWidth: true },
                  { label: 'S3 bucket', value: tenant.s3Bucket, fullWidth: true },
                  { label: 'Tenant ID', value: tenant._id, fullWidth: true },
                  { label: 'Demo tenant', value: tenant.isDemo ? 'Yes' : 'No' },
                  { label: 'Extra employees', value: tenant.extraEmployees },
                  { label: 'Free employees', value: tenant.freeEmployees },
                ]}
              />
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {tab === 'contract' && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <SectionCard
              title="Contract"
              icon={<PaymentsIcon />}
              subtitle={displayContract.accountManager ? `Account manager: ${displayContract.accountManager}` : undefined}
              action={
                <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => setContractOpen(true)}>
                  Edit contract
                </Button>
              }
            >
              <Stack gap={2}>
                {planChangedSinceAcceptance && (
                  <Alert severity="info">
                    The included limits were accepted for {tierLabel(acceptedTier)}, but this
                    tenant is now on {tierLabel(tenant.subscriptionTier)}. The plan defaults apply
                    until the customer accepts the new Plan Summary.
                  </Alert>
                )}
                <KeyValueList
                  columns={3}
                  showEmpty
                  rows={[
                    { label: 'Package', value: displayContract.packageName },
                    {
                      label: 'Billing cadence',
                      value: CADENCE_LABELS[displayContract.billingCadence] ?? displayContract.billingCadence,
                    },
                    { label: 'Currency', value: displayContract.currency },
                    { label: 'Monthly fee', value: formatCurrency(displayContract.monthlyFee, displayContract.currency) },
                    { label: 'Annual fee', value: formatCurrency(displayContract.annualFee, displayContract.currency) },
                    { label: 'Contract start', value: formatDate(displayContract.contractStartDate) },
                    { label: 'Renewal date', value: formatDate(displayContract.renewalDate) },
                    ...ALLOWANCE_METRICS.map((metric) => ({
                      label: metric.label,
                      value: contractLimitText(tenant, displayContract, metric),
                    })),
                    legacyLimits.length
                      ? {
                          label: 'Legacy agreed limits',
                          value: `${legacyLimits.map((limit) => `${limit.label}: ${formatNumber(limit.value)}`).join(' · ')} - a historic override until included limits are set`,
                          fullWidth: true,
                        }
                      : null,
                    {
                      label: 'Usage review',
                      value: displayContract.usageReview?.enabled
                        ? `Enabled · ${usageStatus.label.toLowerCase()}`
                        : 'Disabled',
                    },
                    { label: 'Quote reference', value: displayContract.quote?.quoteReference },
                    { label: 'Quote accepted', value: displayContract.quote?.acceptedAt ? `${formatDate(displayContract.quote.acceptedAt)}${displayContract.quote.acceptedBy ? ` by ${displayContract.quote.acceptedBy}` : ''}` : '' },
                    { label: 'Contract notes', value: displayContract.notes, fullWidth: true },
                    { label: 'Usage review notes', value: displayContract.usageReview?.notes, fullWidth: true },
                  ]}
                />
                <Typography variant="caption" color="text.secondary">
                  Limits show what the contract stores (a plan default where nothing of its own is
                  stored). Effective allowances, complimentary capacity, the agreement and the
                  usage review are on the{' '}
                  <Link component="button" type="button" variant="caption" onClick={() => setTab('usage')}>
                    Commercial usage
                  </Link>{' '}
                  tab.
                </Typography>
              </Stack>
            </SectionCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Stack gap={2}>
              <SectionCard title="Stripe" icon={<CreditCardIcon />}>
                <Stack gap={1.5}>
                  <KeyValueList
                    columns={1}
                    showEmpty
                    rows={[
                      { label: 'Customer ID', value: displayContract.stripe?.customerId },
                      { label: 'Subscription ID', value: displayContract.stripe?.subscriptionId },
                      { label: 'Price ID', value: displayContract.stripe?.priceId },
                      { label: 'Status', value: tenant.stripeSubscriptionStatus || displayContract.stripe?.status },
                    ]}
                  />
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button size="small" variant="outlined" onClick={ensureStripeCustomer} disabled={busy}>
                      {tenant.stripeCustomerRef || displayContract.stripe?.customerId ? 'Sync Stripe customer' : 'Create Stripe customer'}
                    </Button>
                    <Button size="small" variant="outlined" onClick={openStripe} disabled={!hasStripe} endIcon={<OpenInNewIcon />}>
                      Open in Stripe
                    </Button>
                  </Stack>
                </Stack>
              </SectionCard>
              <SectionCard title="Enterprise+" icon={<LocalShippingIcon />}>
                <Stack gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Start a quoted Enterprise+ subscription in Stripe and move this tenant onto
                    Enterprise with the agreed journey allowance. For a tenant that already has a
                    subscription, change it by hand in Stripe and record it with Complete
                    Enterprise+ transition on the Commercial usage tab.
                  </Typography>
                  <Button size="small" variant="contained" onClick={() => setQuotedOpen(true)} disabled={busy}>
                    Start Enterprise+ subscription
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tenant.manualBilling === true}
                        onChange={(event) => toggleManualBilling(event.target.checked)}
                        disabled={busy}
                      />
                    }
                    label="Manual billing - access never expires automatically"
                  />
                  <Typography variant="caption" color="text.secondary">
                    For Enterprise+ contracts invoiced outside Stripe (e.g. annual BACS). Enabling
                    it puts a tenant with no paid tier on Enterprise, and billing is emailed 30 and
                    7 days before the contract renewal date so the next invoice can be raised by
                    hand.
                  </Typography>
                  {tenant.manualBilling === true && hasLiveStripeSubscription && (
                    <Alert severity="warning">
                      This tenant also has a live Stripe subscription, which will keep billing.
                      Manual billing is normally for tenants invoiced outside Stripe - cancel the
                      Stripe subscription or switch manual billing off.
                    </Alert>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tenant.shortPaymentGrace === true}
                        onChange={(event) => togglePaymentGrace(event.target.checked)}
                        disabled={busy || tenant.manualBilling === true}
                      />
                    }
                    label="48-hour payment grace - pay promptly after expiry"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {tenant.manualBilling === true
                      ? 'Unavailable while manual billing is on - manually billed tenants never expire automatically.'
                      : 'Caps the normal renewal/payment grace: once the paid-through date passes, this tenant is paywalled after 48 hours whatever grace Stripe collection would normally allow. Billing and configuration pages stay reachable so they can pay.'}
                  </Typography>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>
      )}

      {tab === 'usage' && (
        <CommercialUsagePanel
          tenantID={tenantID}
          tenant={tenant}
          hasStripe={hasStripe}
          onOpenStripe={openStripe}
          onLoaded={absorbUsagePanel}
          onChanged={(panel) => {
            absorbUsagePanel(panel)
            reloadTenant()
          }}
        />
      )}

      {tab === 'users' && (
        <UserAccounts tenantID={tenantID} subdomain={tenant.subdomain} users={users} loading={usersLoading} />
      )}

      {tab === 'sales' && (
        <Grid container spacing={2}>
          <Grid item xs={12} lg={7}>
            <SectionCard
              title="Sales lead"
              icon={<InsightsIcon />}
              action={
                linkedLead ? (
                  <Button size="small" variant="outlined" onClick={openOrCreateLead} disabled={busy}>
                    Open lead
                  </Button>
                ) : (
                  <>
                    <Button size="small" variant="outlined" onClick={() => setLinkLeadOpen(true)} disabled={busy || tenant.isDemo}>
                      Link existing lead
                    </Button>
                    <Button size="small" variant="contained" onClick={openOrCreateLead} disabled={busy}>
                      Create sales lead
                    </Button>
                  </>
                )
              }
            >
              {linkedLead ? (
                <Stack gap={2}>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
                    <StageChip stage={salesStage} />
                    <InterestChip level={linkedLead.interestLevel} />
                    {linkedLead.source && <SoftChip color="#64748B" label={linkedLead.source} />}
                    {linkedLead.nextFollowUpAt && (() => {
                      const state = followUpState(linkedLead.nextFollowUpAt)
                      return <SoftChip color={state.color} label={`Follow-up: ${state.key === 'upcoming' ? formatDate(linkedLead.nextFollowUpAt) : state.label}`} />
                    })()}
                  </Stack>
                  <KeyValueList
                    rows={[
                      { label: 'Contact', value: linkedLead.contactName },
                      { label: 'Email', value: linkedLead.contactEmail },
                      { label: 'Last contacted', value: linkedLead.lastContactedAt ? `${formatDate(linkedLead.lastContactedAt)} (${fromNow(linkedLead.lastContactedAt)})` : 'Never' },
                      { label: 'Lead created', value: formatDate(linkedLead.createdAt) },
                      { label: 'Staff numbers', value: linkedLead.staffNumbers },
                      { label: 'Likely plan', value: tierLabel(linkedLead.likelyTier, '') },
                      { label: 'Notes', value: linkedLead.notes, fullWidth: true },
                    ]}
                  />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No sales lead is linked to this tenant yet. If this organisation was already in
                  the pipeline, link that lead so its notes and timeline stay with them; otherwise
                  create one. Either way the lead carries the tenant&apos;s details across if the
                  tenant is later deleted.
                </Typography>
              )}
            </SectionCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <SectionCard title="Stage" subtitle="Moving to Lost disables the tenant; moving back re-enables it.">
              <FormControl size="small" fullWidth>
                <InputLabel>Sales stage</InputLabel>
                <Select
                  value={salesStage}
                  label="Sales stage"
                  disabled={busy}
                  onChange={(event) => changeStage(event.target.value)}
                >
                  {STAGES.map((stage) => (
                    <MenuItem key={stage.key} value={stage.key}>
                      {stage.label}
                    </MenuItem>
                  ))}
                  {salesStage === 'Trial Started' && <MenuItem value="Trial Started">Trialled (legacy)</MenuItem>}
                </Select>
              </FormControl>
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {tab === 'tools' && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SectionCard title="Bulk employee import" subtitle="Upload a CSV of staff to create their accounts and employee records">
              <BulkEmployeeImport tenantID={tenantID} />
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {tab === 'activity' && (
        <SectionCard title="Activity" icon={<TimelineIcon />} subtitle="Internal notes about this tenant">
          <ActivityTimeline items={tenant.comments ?? []} onSubmit={logActivity} />
        </SectionCard>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null)
            openOrCreateLead()
          }}
        >
          {linkedLead ? 'Open sales lead' : 'Create sales lead'}
        </MenuItem>
        {!linkedLead && !tenant.isDemo && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              setLinkLeadOpen(true)
            }}
          >
            Link existing lead
          </MenuItem>
        )}
        {salesStage !== 'Lost' && !tenant.isDemo && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              setConfirm('lost')
            }}
          >
            Mark lost & disable tenant
          </MenuItem>
        )}
        {!tenant.isDemo && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setMenuAnchor(null)
              setConfirmation('')
              setDeleteLead(false)
              setConfirm('delete')
            }}
          >
            Delete tenant & database
          </MenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={confirm === 'lost'}
        title={`Mark ${tenant.name} as lost`}
        confirmLabel="Mark lost & disable tenant"
        confirmColor="error"
        busy={busy}
        error={error}
        onClose={() => setConfirm(null)}
        onConfirm={markLost}
      >
        <Alert severity="info">
          {tenant.name} will be disabled so nobody can log in. Nothing is deleted - their user
          count and last activity are recorded on the sales lead, and moving the stage away from
          Lost re-enables the tenant. Deleting the database is a separate action.
        </Alert>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Delete ${tenant.name} completely`}
        confirmLabel="Delete tenant & database"
        confirmColor="error"
        busy={busy}
        error={error}
        onClose={() => setConfirm(null)}
        onConfirm={deleteTenant}
      >
        <Alert severity="error">
          This permanently drops the tenant database and removes the tenant record. It cannot be
          undone.
        </Alert>
        <FormControl fullWidth size="small">
          <InputLabel>Sales lead</InputLabel>
          <Select value={deleteLead ? 'delete' : 'keep'} label="Sales lead" onChange={(event) => setDeleteLead(event.target.value === 'delete')}>
            <MenuItem value="keep">Keep the sales lead (snapshot users, activity & trial date onto it)</MenuItem>
            <MenuItem value="delete">Delete the linked sales lead too (test customers)</MenuItem>
          </Select>
        </FormControl>
        <TextField
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          label={`Type "${tenant.name}" to confirm`}
          size="small"
          fullWidth
          autoComplete="off"
        />
      </ConfirmDialog>

      <ContractDialog
        open={contractOpen}
        tenant={tenant}
        onClose={() => setContractOpen(false)}
        onSaved={(updated) => {
          mergeTenant(updated)
          // The contract changed: fall back to its own summary until the
          // Commercial usage tab is opened again.
          setUsageSummary(null)
          setContractOpen(false)
        }}
      />
      <QuotedSubscriptionDialog
        open={quotedOpen}
        tenant={tenant}
        contract={displayContract}
        onClose={() => setQuotedOpen(false)}
        onStarted={(updated) => {
          mergeTenant(updated)
          setUsageSummary(null)
          setQuotedOpen(false)
        }}
      />
      <LinkLeadDialog
        open={linkLeadOpen}
        tenant={tenant}
        onClose={() => setLinkLeadOpen(false)}
        onLinked={(lead) => {
          setLinkLeadOpen(false)
          setTenant((prev) => ({
            ...prev,
            linkedCRMRecord: lead,
            isActive: lead.stage === 'Lost' ? false : prev.isActive,
          }))
        }}
      />
    </Stack>
  )
}

export default SingleTenant
